/* eslint-disable react/no-unknown-property */
import type { FontsComponentProps } from './PreloadFonts';
import type { FC } from 'react';

import Head from 'next/head';

const FINGER_PRINT = '?v=251031';

const LATIN_UNICODE_RANGE =
  'U+0020-007E,U+00A0-017E,U+01E6-01E7,U+021A-021B,U+02C6-02C7,U+02D8-02DA,U+02DC-02DD,U+0300-0304,U+0306-0308,U+030A-030C,U+0326-0327,U+03BC,U+0440,U+2013,U+2018-201A,U+201C-201E,U+2020-2022,U+2026,U+2032-2033,U+2039-203A,U+2044,U+2070,U+2074-2079,U+2080-2089,U+20A1,U+20A4,U+20A6-20A7,U+20A9-20AC,U+20B4-20B5,U+20B8-20BA,U+2122,U+2153-215E,U+223C,U+FB00-FB04';

const CYRILLIC_ONLY_UNICODE_RANGE = 'U+0400-045F,U+048A-052F,U+2116';

// DINPro_normal_condensed_700_subset1.woff2 has no Cyrillic-only variant planned, so its
// unicode-range is limited to codepoints confirmed present in that file's own cmap (verified
// against the live asset), rather than the full Latin + Cyrillic-only ranges above.
const DIN_PRO_UNICODE_RANGE =
  'U+0020-007E,U+00A0-017E,U+021A-021B,U+02C6-02C7,U+02D8-02DA,U+02DC-02DD,U+03BC,U+0400-045F,U+0490-0493,U+0496-0497,U+049A-049D,U+04A2-04A3,U+04AE-04B3,U+04B6-04BB,U+04C0,U+04CF,U+04D8-04D9,U+04E2-04E3,U+04E8-04E9,U+04EE-04EF,U+2013,U+2018-201A,U+201C-201E,U+2020-2022,U+2026,U+2039-203A,U+2044,U+2070,U+2074-2079,U+2080-2089,U+20AC,U+20B4,U+2116,U+2122,U+2153-215E,U+FB01-FB02';

const fontface = (fontAssetPath: string) => `
@font-face {
  font-family: 'DIN Pro';
  src: url('${fontAssetPath}/DINPro_normal_condensed_700_subset1.woff2${FINGER_PRINT}') format('woff2');
  font-weight: 700;
  font-display: swap;
  font-stretch: normal;
  unicode-range: ${DIN_PRO_UNICODE_RANGE};
}
@font-face {
  font-family: 'Gotham HCo';
  src: url('${fontAssetPath}/GothamBold_normal_normal_subset1.woff2') format('woff2');
  font-weight: 700;
  font-display: swap;
  font-stretch: normal;
  unicode-range: ${LATIN_UNICODE_RANGE};
}
@font-face {
  font-family: 'Gotham HCo';
  src: url('${fontAssetPath}/GothamBold_normal_normal_subset_cy-only.woff2') format('woff2');
  font-weight: 700;
  font-display: swap;
  font-stretch: normal;
  unicode-range: ${CYRILLIC_ONLY_UNICODE_RANGE};
}

@font-face {
  font-family: 'Gotham HCo';
  src: url('${fontAssetPath}/GothamMedium_normal_normal_subset1.woff2') format('woff2');
  font-weight: 500 600;
  font-display: swap;
  font-stretch: normal;
  unicode-range: ${LATIN_UNICODE_RANGE};
}
@font-face {
  font-family: 'Gotham HCo';
  src: url('${fontAssetPath}/GothamMedium_normal_normal_subset_cy-only.woff2') format('woff2');
  font-weight: 500 600;
  font-display: swap;
  font-stretch: normal;
  unicode-range: ${CYRILLIC_ONLY_UNICODE_RANGE};
}

@font-face {
  font-family: 'Gotham HCo';
  src: url('${fontAssetPath}/GothamBook_normal_normal_subset1.woff2') format('woff2');
  font-weight: 400;
  font-display: swap;
  font-stretch: normal;
  unicode-range: ${LATIN_UNICODE_RANGE};
}
@font-face {
  font-family: 'Gotham HCo';
  src: url('${fontAssetPath}/GothamBook_normal_normal_subset_cy-only.woff2') format('woff2');
  font-weight: 400;
  font-display: swap;
  font-stretch: normal;
  unicode-range: ${CYRILLIC_ONLY_UNICODE_RANGE};
}
`;

export const GSTARFonts: FC<FontsComponentProps> = ({ fontAssetPath }) => (
  <Head>
    <link
      rel="preload"
      href={`${fontAssetPath}/GothamBook_normal_normal_subset1.woff2`}
      as="font"
      type="font/woff2"
      fetchPriority="high"
      crossOrigin="anonymous"
    />
    <link
      rel="preload"
      href={`${fontAssetPath}/GothamMedium_normal_normal_subset1.woff2`}
      as="font"
      type="font/woff2"
      fetchPriority="high"
      crossOrigin="anonymous"
    />
    <link
      rel="preload"
      href={`${fontAssetPath}/GothamBold_normal_normal_subset1.woff2`}
      as="font"
      type="font/woff2"
      fetchPriority="high"
      crossOrigin="anonymous"
    />
    <link
      rel="preload"
      href={`${fontAssetPath}/DINPro_normal_condensed_700_subset1.woff2${FINGER_PRINT}`}
      as="font"
      type="font/woff2"
      fetchPriority="high"
      crossOrigin="anonymous"
    />
    {/* eslint-disable-next-line react/no-danger */}
    <style type="text/css" dangerouslySetInnerHTML={{ __html: fontface(fontAssetPath) }} />
  </Head>
);
